import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button, Form, InputGroup,
  Alert, Spinner, Badge, ListGroup
} from 'react-bootstrap';
import {
  Ruler, Search, User, Phone, X, ChevronRight, Scissors, UserPlus, ArrowLeft
} from 'lucide-react';

import { useNavigate } from 'react-router-dom';


import MeasurementsTab from './MeasurementsTab';
import NewCustomerMeasurementModal from './NewCustomerMeasurementModal';
import customerService from '../../../services/customerService';
import { useAuth } from '../../../contexts/AuthContext';
import { useBoutique } from '../../../contexts/BoutiqueContext';

/**
 * ✅ ATELIER: Page dédiée à la gestion des mesures clients.
 * Accessible via /atelier/measurements dans le menu Atelier.
 */
const AtelierMeasurementsPage = () => {
  const { user } = useAuth();
  const { isAtelierEnabled } = useBoutique();
  const isManager = user?.role === 'MANAGER';
  const navigate = useNavigate(); // ✅ AJOUT


  // Liste des clients
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Recherche
  const [searchTerm, setSearchTerm] = useState('');

  // Client sélectionné
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // ✅ Modal de création conjointe (nouveau client + fiche mesures)
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);

  useEffect(() => {
    if (isAtelierEnabled) {
      loadCustomers();
    }
  }, [isAtelierEnabled]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await customerService.getCustomers();

      if (response.success) {
        const formattedCustomers = response.data.map(c =>
          customerService.formatCustomerForDisplay(c)
        );
        setCustomers(formattedCustomers);
      } else {
        setError(response.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('❌ Erreur chargement clients:', err);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  /**
   * ✅ Quand on a créé une fiche + client automatiquement
   */
  const handleNewCustomerMeasurementSuccess = (newMeasurement, message) => {
    console.log('✅ Fiche + cliene crées:', newMeasurement);

    // Recharger la liste des clients pour inclure la nouvelle
    loadCustomers();

    // Sélectionner automatiquement ce client
    if (newMeasurement?.customerId) {
      setSelectedCustomer({
        id: newMeasurement.customerId,
        name: newMeasurement.customerName,
        phone: newMeasurement.customerPhone
      });
    }

    // Afficher un message de confirmation
    if (message) {
      setSuccess(message);
      setTimeout(() => setSuccess(null), 4000);
    }
  };

  // Filtrage local des clients selon la recherche
  const filteredCustomers = searchTerm.trim()
    ? customers.filter(c => {
        const q = searchTerm.trim().toLowerCase();
        return (
          c.name.toLowerCase().includes(q) ||
          (c.phone && c.phone.toLowerCase().includes(q))
        );
      })
    : customers;

  // Si atelier désactivé
  if (!isAtelierEnabled) {
    return (
      <Container fluid className="py-4">
        <Card className="text-center py-5">
          <Card.Body>
            <Scissors size={64} className="text-muted mb-3" />
            <h4>Module Atelier non activé</h4>
            <p className="text-muted">
              Le module Atelier n'est pas activé pour cette boutique.
              Contactez votre administrateur.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* En-tête */}
      <Row className="mb-4">
  <Col>
    {/* ✅ Bouton retour au dashboard atelier */}
    <Button
      variant="link"
      onClick={() => navigate('/atelier')}
      className="p-0 mb-2 text-decoration-none d-flex align-items-center gap-1"
    >
      <ArrowLeft size={16} />
      <small>Retour au tableau de bord Atelier</small>
    </Button>

    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
      <div>
        <h1 className="h3 mb-1 d-flex align-items-center gap-2">
          <Ruler size={28} className="text-primary" />
          Mesures clients
        </h1>
        <p className="text-muted mb-0">
          Sélectionnez une cliente pour gérer ses fiches de mesures
        </p>
      </div>
      ...

            {/* ✅ Groupe de boutons à droite */}
            <div className="d-flex gap-2 flex-wrap">
              {/* Bouton Nouveau client + mesures */}
              <Button
                variant="primary"
                onClick={() => setShowNewCustomerModal(true)}
                className="d-flex align-items-center gap-2"
              >
                <UserPlus size={18} />
                <span className="d-none d-sm-inline">Nouveau client + mesures</span>
                <span className="d-sm-none">Nouvelle</span>
              </Button>

              {/* Bouton Changer de client (si une est sélectionnée) */}
              {selectedCustomer && (
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setSelectedCustomer(null);
                    setSearchTerm('');
                  }}
                >
                  <X size={16} className="me-1" />
                  <span className="d-none d-sm-inline">Changer</span>
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Alertes */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          ✅ {success}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Chargement des clients...</div>
        </div>
      ) : (
        <Row>
          {/* Colonne gauche : sélecteur de client */}
          <Col lg={selectedCustomer ? 4 : 12} className="mb-4">
            <Card className="shadow-sm">
              <Card.Header className="bg-light">
                <div className="d-flex align-items-center gap-2">
                  <User size={18} className="text-primary" />
                  <strong>Clients</strong>
                  <Badge bg="secondary" pill className="ms-auto">
                    {customers.length}
                  </Badge>
                </div>
              </Card.Header>

              <Card.Body className="p-3 border-bottom">
                <InputGroup size="sm">
                  <InputGroup.Text>
                    <Search size={14} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Rechercher nom ou téléphone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setSearchTerm('')}
                    >
                      <X size={14} />
                    </Button>
                  )}
                </InputGroup>
              </Card.Body>

              <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {filteredCustomers.length === 0 ? (
                  <div className="text-center p-4 text-muted">
                    <User size={40} className="mb-2 opacity-50" />
                    <div>
                      {searchTerm
                        ? 'Aucun client trouvée'
                        : 'Aucun client enregistrée'}
                    </div>
                    {/* ✅ Bouton de création quand aucun client */}
                    {!searchTerm && customers.length === 0 && (
                      <Button
                        variant="primary"
                        size="sm"
                        className="mt-3"
                        onClick={() => setShowNewCustomerModal(true)}
                      >
                        <UserPlus size={16} className="me-1" />
                        Créer le premièr client
                      </Button>
                    )}
                  </div>
                ) : (
                  <ListGroup variant="flush">
                    {filteredCustomers.map(customer => {
                      const isSelected = selectedCustomer?.id === customer.id;
                      return (
                        <ListGroup.Item
                          key={customer.id}
                          action
                          active={isSelected}
                          onClick={() => setSelectedCustomer(customer)}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <div className="flex-grow-1">
                            <div className="fw-semibold">{customer.name}</div>
                            {customer.phone && (
                              <small className={isSelected ? 'text-white-50' : 'text-muted'}>
                                <Phone size={10} className="me-1" />
                                {customer.phone}
                              </small>
                            )}
                          </div>
                          <ChevronRight size={16} />
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                )}
              </div>
            </Card>
          </Col>

          {/* Colonne droite : mesures du client sélectionnée */}
          {selectedCustomer && (
            <Col lg={8}>
              <Card className="shadow-sm">
                <Card.Body>
                  <MeasurementsTab
                    customerId={selectedCustomer.id}
                    customerName={selectedCustomer.name}
                    isManager={isManager}
                  />
                </Card.Body>
              </Card>
            </Col>
          )}

          {/* Message si aucun client sélectionnée mais qu'il y en a dans la liste */}
          {!selectedCustomer && customers.length > 0 && (
            <Col lg={12}>
              <Card className="text-center py-5 shadow-sm border-dashed">
                <Card.Body>
                  <Ruler size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">Sélectionnez un client</h5>
                  <p className="text-muted mb-0">
                    Cliquez sur un client dans la liste pour consulter ou créer ses fiches de mesures.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* ✅ MODAL : Création conjointe nouveau client + mesures */}
      <NewCustomerMeasurementModal
        show={showNewCustomerModal}
        onHide={() => setShowNewCustomerModal(false)}
        onSuccess={handleNewCustomerMeasurementSuccess}
      />

      <style>{`
        .border-dashed {
          border: 2px dashed #e2e8f0 !important;
        }
      `}</style>
    </Container>
  );
};

export default AtelierMeasurementsPage;